import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SectionBackground from './SectionBackground';

const CTASection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <SectionBackground 
      variant="white"
      style={{
        padding: 'clamp(40px, 6vw, 60px) clamp(20px, 5vw, 40px)'
      }}>

      <div className="container" style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        position: 'relative',
        textAlign: 'center'
      }}>
        {/* Clean CTA card */}
        <div style={{
          position: 'relative',
          padding: '80px 60px',
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(0, 82, 255, 0.1)',
          borderRadius: '24px',
          boxShadow: '0 2px 8px rgba(0, 82, 255, 0.08)',
          backdropFilter: 'blur(10px)'
        }}>

          <h2 style={{
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 900,
            marginBottom: '30px',
            color: '#0A0B0D',
            lineHeight: 1.2
          }}>
{t('landing.cta.title')}
          </h2>

          <p style={{
            fontSize: 'clamp(0.9rem, 2.5vw, 1.125rem)',
            color: '#5B616E',
            marginBottom: '50px',
            maxWidth: '600px',
            margin: '0 auto 50px'
          }}>
{t('landing.cta.subtitle')}
          </p>

          {/* Value proposition cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '20px',
            marginBottom: '50px',
            maxWidth: '800px',
            margin: '0 auto 50px'
          }}>
            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #F8F9FF 0%, #E7F3FF 100%)',
              border: '1px solid rgba(0, 82, 255, 0.15)',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>⚡</div>
              <div style={{ color: '#0052FF', fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>
                5 Minutes
              </div>
              <div style={{ color: '#5B616E', fontSize: '0.85rem' }}>
                Create your lease
              </div>
            </div>

            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #F8F9FF 0%, #E7F3FF 100%)',
              border: '1px solid rgba(0, 82, 255, 0.15)',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>✓</div>
              <div style={{ color: '#0052FF', fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>
                California-focused
              </div>
              <div style={{ color: '#5B616E', fontSize: '0.85rem' }}>
                Tailored prompts for CA rentals
              </div>
            </div>

            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
              border: '2px solid #10B981',
              borderRadius: '16px',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                padding: '4px 16px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'white',
                whiteSpace: 'nowrap'
              }}>
                BEST VALUE
              </div>
              <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🎉</div>
              <div style={{ color: '#059669', fontSize: '1.25rem', fontWeight: 900, marginBottom: '4px' }}>
                FREE
              </div>
              <div style={{ color: '#5B616E', fontSize: '0.85rem' }}>
                Free for Landlords
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/signup')}
            aria-label="Start creating your lease now"
            style={{
              padding: '18px 40px',
              background: 'linear-gradient(135deg, #0052FF 0%, #00D4FF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '999px',
              fontSize: '1.1rem',
              fontWeight: 700,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 82, 255, 0.15)',
              textTransform: 'none',
              letterSpacing: '0.5px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 82, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 82, 255, 0.15)';
            }}
          >
            <span style={{ position: 'relative', zIndex: 1 }}>
{t('landing.cta.button')} →
            </span>
            
            {/* Animated shine effect */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'rgba(255, 255, 255, 0.1)',
              animation: 'shine 3s infinite'
            }} />
          </button>

          {/* Trust badges */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            marginTop: '40px',
            flexWrap: 'wrap'
          }}>
            {[
              '🔒 Private by default',
              '⚡ Instant access',
              '🧭 Built for California rentals'
            ].map((badge, i) => (
              <div
                key={i}
                style={{
                  color: '#5B616E',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                {badge}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shine {
          0% { transform: translateX(-100%) translateY(-100%); }
          100% { transform: translateX(100%) translateY(100%); }
        }

        @media (max-width: 768px) {
          .container > div {
            padding: 50px 30px !important;
          }
        }

        @media (max-width: 480px) {
          .container > div {
            padding: 40px 20px !important;
          }
        }
      `}</style>
    </SectionBackground>
  );
};

export default CTASection; 
